
import { createSharedComposable, useEventListener } from '@vueuse/core';

import { useNonComponentHook } from '@/composables/useHookPair';

const TYPE_LIFECYCLE = 'TYPE_LIFECYCLE';
const LIFECYCLE_READY_RECEIVE_MSG = 'LIFECYCLE_READY_RECEIVE_MSG';
const LIFECYCLE_LIVE_STARTED = 'LIFECYCLE_LIVE_STARTED';
const LIFECYCLE_LIVE_ENDED = 'LIFECYCLE_LIVE_ENDED';

const PROMOTING_PRODUCT_CHANGED = 'PROMOTING_PRODUCT_CHANGED';
const PROMOTING_PRODUCT_CLICKED = 'PROMOTING_PRODUCT_CLICKED';

const PRODUCT_CHANGED = 'PRODUCT_CHANGED';
const PRODUCTS_SALABLE_STATUS = 'PRODUCTS_SALABLE_STATUS';

const AUDIO_STATUS_CHANGED = 'AUDIO_STATUS_CHANGED';
const PLAY_STATUS_CHANGED = 'PLAY_STATUS_CHANGED';
const SELECTED_CLIP_CHANGED = 'SELECTED_CLIP_CHANGED';
const VIDEO_CURRENT_TIME_CHANGED = 'VIDEO_CURRENT_TIME_CHANGED';
const VIDEO_EL_BOUNDED_STATUS_CHANGED = 'VIDEO_EL_BOUNDED_STATUS_CHANGED';

const FORCE_RELOAD = 'FORCE_RELOAD';

const SEND_PUBNUB_ACTUAL_ID = 'SEND_PUBNUB_ACTUAL_ID';
const CHILD_FRAME_FINGERPRINT_PRO_ID = 'CHILD_FRAME_FINGERPRINT_PRO_ID';

export const usePostMessageReceiver = createSharedComposable(() => {
  const [triggerStreamReady, onStreamReady] = useNonComponentHook();
  const [triggerVideoElBoundStatusChanged, onVideoElBoundStatusChanged] = useNonComponentHook();
  const [triggerLiveStarted, onLiveStarted] = useNonComponentHook();
  const [triggerLiveEnded, onLiveEnded] = useNonComponentHook();
  const [triggerPromotingProductChanged, onPromotingProductChanged] = useNonComponentHook();
  const [triggerPromotingProductClicked, onPromotingProductClicked] = useNonComponentHook();
  const [triggerProductChanged, onProductChanged] = useNonComponentHook();
  const [triggerProductSalableStatus, onProductSalableStatus] = useNonComponentHook();
  const [triggerAudioStatusChanged, onAudioStatusChanged] = useNonComponentHook();
  const [triggerPlayStatusChanged, onPlayStatusChanged] = useNonComponentHook();
  const [triggerSelectedClipChanged, onSelectedClipChanged] = useNonComponentHook();
  const [triggerVideoCurrentTimeChanged, onVideoCurrentTimeChanged] = useNonComponentHook();
  const [triggerForcedReload, onForcedReload] = useNonComponentHook();
  const [triggerSendPubnubActualId, onSendPubnubActualId] = useNonComponentHook();
  const [triggerChildFrameFingerprintProId, onChildFrameFingerprintProId] = useNonComponentHook();

  useEventListener('message', (event) => {
    if (event?.data?.type === TYPE_LIFECYCLE && event?.data?.data === LIFECYCLE_READY_RECEIVE_MSG) {
      triggerStreamReady(event);
    } else if (event?.data?.type === TYPE_LIFECYCLE && event?.data?.data === LIFECYCLE_LIVE_STARTED) {
      triggerLiveStarted(undefined);
    } else if (event?.data?.type === TYPE_LIFECYCLE && event?.data?.data === LIFECYCLE_LIVE_ENDED) {
      triggerLiveEnded(undefined);
    } else if (event?.data?.type === PROMOTING_PRODUCT_CHANGED) {
      triggerPromotingProductChanged(event?.data?.data);
    } else if (event?.data?.type === PROMOTING_PRODUCT_CLICKED) {
      triggerPromotingProductClicked(event?.data?.data);
    } else if (event?.data?.type === PRODUCT_CHANGED) {
      triggerProductChanged(event?.data?.data);
    } else if (event?.data?.type === PRODUCTS_SALABLE_STATUS) {
      triggerProductSalableStatus(event?.data?.data);
    } else if (event?.data?.type === AUDIO_STATUS_CHANGED) {
      triggerAudioStatusChanged(event?.data?.data);
    } else if (event?.data?.type === PLAY_STATUS_CHANGED) {
      triggerPlayStatusChanged(event?.data?.data);
    } else if (event?.data?.type === SELECTED_CLIP_CHANGED) {
      triggerSelectedClipChanged(event?.data?.data);
    } else if (event?.data?.type === VIDEO_CURRENT_TIME_CHANGED) {
      triggerVideoCurrentTimeChanged(event?.data?.data);
    } else if (event?.data?.type === VIDEO_EL_BOUNDED_STATUS_CHANGED) {
      triggerVideoElBoundStatusChanged(event?.data?.data);
    } else if (event?.data?.type === FORCE_RELOAD) {
      triggerForcedReload(undefined);
    } else if (event?.data?.type === SEND_PUBNUB_ACTUAL_ID) {
      triggerSendPubnubActualId(event.data.data);
    } else if (event?.data?.type === CHILD_FRAME_FINGERPRINT_PRO_ID) {
      triggerChildFrameFingerprintProId(event.data.data);
    }
  });

  return {
    /** @type {(cb: (event?: MessageEvent<any>) => void) => void} */
    onStreamReady,
    /** @type {(cb: () => void) => void} */
    onLiveStarted,
    /** @type {(cb: () => void) => void} */
    onLiveEnded,

    /** @type {(cb: (productLikes?: Array<{id?:number;}>) => void) => void} */
    onPromotingProductChanged,
    /** @type {(cb: (productLike?: {id?:number;}) => void) => void} */
    onPromotingProductClicked,

    /**
     * @typedef {import('../js/api').Product} Product
     * @type {(cb: (products?: Product[]) => void) => void}
     */
    onProductChanged,
    /**
     * @typedef {import('../js/api').Product} Product
     * @type {(cb: (updateProducts?: Product[]) => void) => void}
     */
    onProductSalableStatus,

    /** @type {(cb: (muted?: boolean) => void) => void} */
    onAudioStatusChanged,

    /** @type {(cb: (played?: boolean) => void) => void} */
    onPlayStatusChanged,

    /**
     * @typedef {import('../js/api').ArchiveClipsData} ArchiveClipsData
     * @type {(cb: (clip: ArchiveClipsData | null) => void) => void}
     */
    onSelectedClipChanged,

    /** @type {(cb: (time: number) => void) => void} */
    onVideoCurrentTimeChanged,

    /** @type {(cb: (isBound: boolean) => void) => void} */
    onVideoElBoundStatusChanged,

    /** @type {(cb: () => void) => void} */
    onForcedReload,

    /** @type {(cb: (data?: {pn_actual_id: string}) => void) => void} */
    onSendPubnubActualId,

    /** @type {(cb: (fingerprintProId?: string) => void) => void} */
    onChildFrameFingerprintProId,
  };
});
